import axios, { AxiosError } from 'axios';
import {
  RegisterData,
  RegisterResponse,
  ClientLoginResponse,
  RefreshResponse,
  Client,
  Installation,
  InstallationUpdate,
  InstallationWithApiKey,
  AddInstallationRequest,
  ApiKey,
  UsageData,
  SubmitActualProductionRequest,
  CorrectionFactor,
  CorrectionStats,
  ForecastResponse,
  PerformanceAnalysis,
} from '../types';
import { clientStorage } from '../utils/clientStorage';

function resolveApiUrl(): string {
  const env = import.meta.env.VITE_API_URL;
  if (env) return env;
  const { protocol, hostname } = window.location;
  if (protocol === 'https:') return `https://api.${hostname}`;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '';
  return `http://${hostname}:3003`;
}
const API_URL = resolveApiUrl();

// Event emitter for client auth events
type ClientAuthEventCallback = () => void;
const clientAuthEventListeners: ClientAuthEventCallback[] = [];

export const onClientUnauthorized = (callback: ClientAuthEventCallback) => {
  clientAuthEventListeners.push(callback);
  return () => {
    const index = clientAuthEventListeners.indexOf(callback);
    if (index > -1) {
      clientAuthEventListeners.splice(index, 1);
    }
  };
};

const emitClientUnauthorized = () => {
  clientAuthEventListeners.forEach((callback) => callback());
};

// Create a separate axios instance for portal API calls
// This uses client tokens instead of admin tokens
export const portalApiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add client JWT token
portalApiClient.interceptors.request.use(
  (config) => {
    const token = clientStorage.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh for portal
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

portalApiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & {
      _retry?: boolean;
    };

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest?._retry) {
      // If already refreshing, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return portalApiClient(originalRequest!);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      if (originalRequest) {
        originalRequest._retry = true;
      }
      isRefreshing = true;

      const refreshToken = clientStorage.getRefreshToken();

      if (!refreshToken) {
        // No refresh token, logout
        clientStorage.clearTokens();
        emitClientUnauthorized();
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token
        const response = await axios.post(`${API_URL}/portal/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;
        clientStorage.setAccessToken(accessToken);
        if (newRefreshToken) {
          clientStorage.setRefreshToken(newRefreshToken);
        }

        // Update the original request with new token
        if (originalRequest && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        processQueue(null, accessToken);

        return portalApiClient(originalRequest!);
      } catch (refreshError) {
        // Refresh failed, logout
        processQueue(refreshError as AxiosError, null);
        clientStorage.clearTokens();
        emitClientUnauthorized();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Portal API Functions

/**
 * Validate activation code and get plan info
 */
export async function validateActivationCode(activationCode: string): Promise<{
  valid: boolean;
  plan?: { id: string; name: string; maxStrings: number };
  message?: string;
}> {
  const response = await portalApiClient.post('/portal/validate-activation-code', {
    activationCode,
  });
  return response.data;
}

/**
 * Register a new client with activation code and installation
 */
export async function register(data: RegisterData): Promise<RegisterResponse> {
  const response = await portalApiClient.post('/portal/register', data);
  return response.data;
}

/**
 * Login client and get JWT tokens
 */
export async function loginClient(email: string, password: string): Promise<ClientLoginResponse> {
  const response = await portalApiClient.post('/portal/login', { email, password });
  return response.data;
}

/**
 * Refresh client access token
 */
export async function refreshClientToken(): Promise<RefreshResponse> {
  const refreshToken = clientStorage.getRefreshToken();
  const response = await portalApiClient.post('/portal/refresh', { refreshToken });
  return response.data;
}

/**
 * Get current client profile
 */
export async function getClientProfile(): Promise<Client> {
  const response = await portalApiClient.get('/portal/me');
  return response.data.client;
}

/**
 * Transform backend installation format to frontend flat format
 */
function transformInstallation(inst: Record<string, unknown>): Installation {
  const flat: Record<string, unknown> = {
    id: inst.id,
    clientId: inst.clientId,
    latitude: inst.lat,
    longitude: inst.lon,
    createdAt: inst.createdAt,
    updatedAt: inst.updatedAt,
  };

  const configs = inst.stringConfigs;
  if (Array.isArray(configs)) {
    configs.forEach((cfg: Record<string, number>, i: number) => {
      const n = i + 1;
      flat[`azimuth${n}`] = cfg.azimuth;
      flat[`tilt${n}`] = cfg.tilt;
      flat[`panelCount${n}`] = cfg.panelCount;
      flat[`panelSize${n}`] = cfg.panelSize;
      flat[`efficiency${n}`] = cfg.efficiency;
    });
  }

  return flat as unknown as Installation;
}

/**
 * Get client's installation details
 */
export async function getInstallation(): Promise<Installation | null> {
  const response = await portalApiClient.get('/portal/installation');
  const inst = response.data.installation;
  if (!inst) return null;
  return transformInstallation(inst);
}

/**
 * Update client's installation
 */
export async function updateInstallation(
  installationId: string,
  data: InstallationUpdate
): Promise<Installation> {
  const response = await portalApiClient.put(`/portal/installations/${installationId}`, data);
  return transformInstallation(response.data.installation);
}

/**
 * Get client's API key
 */
export async function getApiKey(): Promise<ApiKey> {
  const response = await portalApiClient.get('/portal/api-key');
  return response.data.apiKey;
}

/**
 * Regenerate client's API key
 */
export async function regenerateApiKey(): Promise<{ newKey: string; apiKey: ApiKey }> {
  const response = await portalApiClient.post('/portal/api-key/regenerate');
  return response.data;
}

/**
 * Get client's usage statistics
 */
export async function getUsageStats(): Promise<UsageData> {
  const response = await portalApiClient.get('/portal/usage');
  return response.data;
}

// Multi-Installation API Functions

/**
 * Get all installations for the current client (with associated API keys)
 */
export async function getInstallations(): Promise<InstallationWithApiKey[]> {
  const response = await portalApiClient.get('/portal/installations');
  const installations = response.data.installations || response.data;
  if (!Array.isArray(installations)) return [];
  return installations.map((inst: Record<string, unknown>) => {
    const transformed = transformInstallation(inst);
    // Backend returns apiKeys (array) — pick first active key
    const apiKeysArr = (inst.apiKeys || []) as Array<Record<string, unknown>>;
    const activeKey = apiKeysArr.find((k) => k.active) || apiKeysArr[0] || null;
    return {
      ...transformed,
      name: (inst.name as string) || undefined,
      apiKey: activeKey
        ? ({
            id: activeKey.id,
            key: activeKey.key,
            expiresAt: activeKey.expiresAt,
            active: activeKey.active,
            plan: activeKey.plan,
          } as ApiKey)
        : undefined,
    };
  });
}

/**
 * Set up first installation (no activation code needed, uses existing API key)
 */
export async function setupFirstInstallation(
  data: Record<string, unknown>
): Promise<{ installation: Installation; apiKey: ApiKey }> {
  const response = await portalApiClient.post('/portal/installations/setup', data);
  return response.data;
}

/**
 * Add a new installation using an activation code
 */
export async function addInstallation(
  data: AddInstallationRequest
): Promise<{ installation: Installation; apiKey: ApiKey }> {
  const response = await portalApiClient.post('/portal/installations/add', data);
  return response.data;
}

/**
 * Rename an installation
 */
export async function renameInstallation(
  installationId: string,
  name: string
): Promise<Installation> {
  const response = await portalApiClient.patch(`/portal/installations/${installationId}/name`, {
    name,
  });
  return response.data.installation || response.data;
}

/**
 * Get all API keys for the current client
 */
export async function getApiKeys(): Promise<ApiKey[]> {
  const response = await portalApiClient.get('/portal/api-keys');
  return response.data.apiKeys || response.data;
}

// Correction API Functions

/**
 * Submit actual production data for a given date.
 * When installationId is provided, targets a specific installation.
 */
export async function submitActualProduction(
  data: SubmitActualProductionRequest,
  installationId?: string
): Promise<{ message: string }> {
  const url = installationId
    ? `/portal/correction/${installationId}/actual`
    : '/portal/correction/actual';
  const response = await portalApiClient.post(url, data);
  return response.data;
}

/**
 * Get correction factors for a specific installation (or default).
 */
export async function getCorrectionFactors(installationId?: string): Promise<{
  factors: CorrectionFactor[];
  correctionDefault: boolean;
}> {
  const url = installationId
    ? `/portal/correction/${installationId}/factors`
    : '/portal/correction/factors';
  const response = await portalApiClient.get(url);
  return response.data;
}

/**
 * Get correction statistics for a given number of days.
 */
export async function getCorrectionStats(
  days: number = 14,
  installationId?: string
): Promise<CorrectionStats> {
  const url = installationId
    ? `/portal/correction/${installationId}/stats?days=${days}`
    : `/portal/correction/stats?days=${days}`;
  const response = await portalApiClient.get(url);
  return response.data;
}

/**
 * Update client's correction preference for a specific installation (or default).
 */
export async function updateCorrectionPreference(
  correctionDefault: boolean,
  installationId?: string
): Promise<{ message: string }> {
  const url = installationId
    ? `/portal/correction/${installationId}/preference`
    : '/portal/correction/preference';
  const response = await portalApiClient.put(url, { correctionDefault });
  return response.data;
}

// Forecast API Functions

/**
 * Fetch forecast data for a client's installation using their API key.
 * Uses raw axios (not portalApiClient) because the forecast API authenticates
 * via API key header, not JWT.
 */
export async function getForecastForInstallation(
  apiKey: string,
  authHeader: string = 'X-API-Key'
): Promise<{ today: ForecastResponse; tomorrow: ForecastResponse }> {
  const baseUrl = resolveApiUrl();

  // `enriched=1` — portal Dashboard chce dodatkowe pola (weather/prices/horizon/...)
  // które są wyłączone z domyślnego publicznego JSON-a (tylko prognoza nasłonecznienia).
  const [todayResponse, tomorrowResponse] = await Promise.all([
    axios.get(`${baseUrl}/?day=today&type=standard&enriched=1`, {
      headers: { [authHeader]: apiKey },
    }),
    axios.get(`${baseUrl}/?day=tomorrow&type=standard&enriched=1`, {
      headers: { [authHeader]: apiKey },
    }),
  ]);

  return {
    today: todayResponse.data as ForecastResponse,
    tomorrow: tomorrowResponse.data as ForecastResponse,
  };
}

// Performance Analysis

export async function getPerformanceAnalysis(
  installationId: string,
  days: number = 30
): Promise<PerformanceAnalysis> {
  const response = await portalApiClient.get(
    `/portal/correction/${installationId}/performance?days=${days}`
  );
  return response.data;
}

/**
 * Pobiera plik (CSV lub PDF) z endpointu eksportu i uruchamia download w przeglądarce.
 */
async function downloadFile(url: string, filename: string): Promise<void> {
  const response = await portalApiClient.get(url, { responseType: 'blob' });
  const blob = new Blob([response.data]);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

export async function downloadForecastHistoryCsv(
  installationId: string,
  days: number = 30
): Promise<void> {
  await downloadFile(
    `/portal/export/forecast-history/${installationId}?days=${days}`,
    `forecast_history_${installationId}_${days}d.csv`
  );
}

export async function downloadMonthlyReportPdf(
  installationId: string,
  year: number,
  month: number
): Promise<void> {
  await downloadFile(
    `/portal/export/monthly-report/${installationId}?year=${year}&month=${month}`,
    `report_${installationId}_${year}-${String(month).padStart(2, '0')}.pdf`
  );
}

export interface BuildingFootprint {
  polygon: Array<{ lat: number; lon: number }>;
  tags: Record<string, string>;
  center: { lat: number; lon: number };
  source: 'osm' | 'none';
}

export async function getBuildingFootprint(installationId: string): Promise<BuildingFootprint> {
  const response = await portalApiClient.get(`/portal/building/${installationId}`);
  return response.data;
}

export async function refreshBuildingFootprint(installationId: string): Promise<BuildingFootprint> {
  const response = await portalApiClient.post(`/portal/building/${installationId}/refresh`);
  return response.data;
}

// Helper function to handle portal API errors
export const handlePortalApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string; errors?: Record<string, string[]> }>;

    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    if (axiosError.response?.data?.errors) {
      const errors = axiosError.response.data.errors;
      const firstError = Object.values(errors)[0];
      return firstError ? firstError[0] : 'Wystąpił błąd';
    }

    if (axiosError.message) {
      return axiosError.message;
    }
  }

  return 'Wystąpił nieoczekiwany błąd';
};
